import React, { useMemo } from "react";
import "./QuestionGridNumber.css";
import useAppHooks from "../../../../shared/hooks/useAppHooks";
import { getExamSectionKey } from "../../../../shared/utilities/helpers/examSectionKey";

export default function QuestionGridNumber({ selectedIndex, onSelect, questions = null }) {
  const { selector } = useAppHooks();
  const { questionsData } = selector((s) => s.examSlice);
  const { practiceSolution } = selector((s) => s.analyticsSlice);

  const analyticsData = practiceSolution?.data || [];

  const data = useMemo(() => {
    if (Array.isArray(questions) && questions.length > 0) {
      return questions;
    }

    return questionsData.map((q) => {
      const analyticsQ = analyticsData.find(
        (a) => String(a.qid) === String(q.qid)
      );

      return {
        ...q,
        is_correct: analyticsQ?.is_correct,
        correct: analyticsQ?.correct,
      };
    });
  }, [analyticsData, questions, questionsData]);

  const pickValue = (...values) => {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return null;
  };

  const hasAttemptedAnswer = (q) => {
    const selectedOptionRaw = pickValue(
      q?.selectedOption,
      q?.selected_option,
      q?.user_selected_option,
      q?.selected_option_id,
      q?.user_option_id
    );

    const hasOptionAnswer = Array.isArray(selectedOptionRaw)
      ? selectedOptionRaw.length > 0
      : selectedOptionRaw !== null &&
        selectedOptionRaw !== undefined &&
        String(selectedOptionRaw).trim() !== "";

    const integerAnswer = String(
      pickValue(
        q?.integerAnswer,
        q?.integer_Answer,
        q?.integer_answer,
        q?.option_int,
        q?.answer_int,
        ""
      )
    ).trim();

    return hasOptionAnswer || integerAnswer !== "";
  };

  const getIntegerAnswers = (q) => {
    const userIntegerAnswer = String(
      pickValue(
        q?.option_int,
        q?.integerAnswer,
        q?.integer_Answer,
        q?.integer_answer,
        q?.user_option_int,
        q?.user_answer_int,
        q?.attempted_answer,
        q?.answer_given,
        ""
      )
    ).trim();

    const correctIntegerAnswer = String(
      pickValue(q?.answer_int, q?.answer, q?.correct_answer, q?.answer_text, "")
    ).trim();

    return { userIntegerAnswer, correctIntegerAnswer };
  };

  // Keep grid classification in sync with QuestionText rendering source of truth.
  const getRenderedStatus = (q) => {
    if (Array.isArray(q?.option) && q.option.length > 0) {
      const selectedOptions = q.option.filter(
        (opt) => Number(opt?.is_user_correct_ans) === 1
      );
      if (selectedOptions.length > 0) {
        const isCorrect = selectedOptions.every(
          (opt) => Number(opt?.is_correct) === 1
        );
        return { hasSignal: true, attempted: true, correct: isCorrect };
      }
    }

    const { userIntegerAnswer, correctIntegerAnswer } = getIntegerAnswers(q);
    if (userIntegerAnswer !== "") {
      const userNum = Number(userIntegerAnswer);
      const correctNum = Number(correctIntegerAnswer);
      const isCorrect =
        correctIntegerAnswer !== "" &&
        (Number.isFinite(userNum) && Number.isFinite(correctNum)
          ? userNum === correctNum
          : userIntegerAnswer === correctIntegerAnswer);

      return { hasSignal: true, attempted: true, correct: isCorrect };
    }

    if (String(pickValue(q?.user_answer, q?.your_answer, "")).toUpperCase() === "WRONG") {
      return { hasSignal: true, attempted: true, correct: false };
    }

    return { hasSignal: false, attempted: false, correct: false };
  };

  const isQuestionAttempted = (q) => {
    const renderedStatus = getRenderedStatus(q);
    if (renderedStatus.hasSignal) {
      return renderedStatus.attempted;
    }

    const attemptedFlag = pickValue(
      q?.is_attempted,
      q?.isAttempted,
      q?.attempted,
      q?.is_answered
    );

    if (attemptedFlag !== null) {
      if (attemptedFlag === true) return true;
      if (attemptedFlag === false) return false;
      const normalized = String(attemptedFlag).trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }

    const statusText = String(
      pickValue(q?.answer_status, q?.result, q?.user_result, q?.status_text, "")
    ).toLowerCase();
    if (statusText.includes("correct")) return true;
    if (statusText.includes("wrong") || statusText.includes("incorrect")) return true;
    if (statusText.includes("unattempt") || statusText.includes("not answered")) {
      return false;
    }

    return hasAttemptedAnswer(q);
  };

  const isQuestionCorrect = (q) => {
    const renderedStatus = getRenderedStatus(q);
    if (renderedStatus.hasSignal) {
      return renderedStatus.correct;
    }

    const directFlag = pickValue(
      q?.is_correct,
      q?.isCorrect,
      q?.correct,
      q?.user_answered_correct,
      q?.is_user_correct
    );
    if (directFlag === true || Number(directFlag) === 1) return true;
    if (directFlag === false || Number(directFlag) === 0) return false;

    const statusText = String(
      pickValue(q?.answer_status, q?.result, q?.user_result, q?.status_text, "")
    ).toLowerCase();
    if (statusText.includes("correct")) return true;
    if (statusText.includes("wrong") || statusText.includes("incorrect")) return false;

    const answerType = Number(q.answer_type_id);
    if (answerType === 2 && Array.isArray(q.option)) {
      const selectedRaw = pickValue(
        q?.selectedOption,
        q?.selected_option,
        q?.user_selected_option,
        q?.selected_option_id,
        q?.user_option_id
      );
      const selectedIds = Array.isArray(selectedRaw)
        ? selectedRaw.map((id) => String(id))
        : selectedRaw !== null &&
          selectedRaw !== undefined &&
          String(selectedRaw).trim() !== ""
        ? [String(selectedRaw)]
        : [];
      const correctIds = q.option
        .filter((opt) => Number(opt.is_correct) === 1)
        .map((opt) => String(opt.option_id));

      if (!selectedIds.length || !correctIds.length) return false;

      const selectedSet = new Set(selectedIds);
      const correctSet = new Set(correctIds);
      if (selectedSet.size !== correctSet.size) return false;
      return [...correctSet].every((id) => selectedSet.has(id));
    }

    if (answerType === 1 && Array.isArray(q.option)) {
      const selectedId = String(
        pickValue(
          q?.selectedOption,
          q?.selected_option,
          q?.user_selected_option,
          q?.selected_option_id,
          q?.user_option_id,
          ""
        )
      ).trim();
      if (!selectedId) return false;
      return q.option.some(
        (opt) =>
          String(opt.option_id) === selectedId && Number(opt.is_correct) === 1
      );
    }

    if (answerType === 3) {
      const userAns = String(
        pickValue(
          q?.integerAnswer,
          q?.integer_Answer,
          q?.integer_answer,
          q?.option_int,
          q?.answer_int,
          ""
        )
      ).trim();
      const correctAns = String(
        pickValue(q?.answer, q?.correct_answer, q?.answer_text, "")
      ).trim();
      if (!userAns || !correctAns) return false;
      const userNum = Number(userAns);
      const correctNum = Number(correctAns);
      if (Number.isFinite(userNum) && Number.isFinite(correctNum)) {
        return userNum === correctNum;
      }
      return userAns === correctAns;
    }

    if (q.is_correct !== undefined && q.is_correct !== null) {
      return Number(q.is_correct) === 1;
    }
    if (q.correct !== undefined && q.correct !== null) {
      const normalized = String(q.correct).trim().toLowerCase();
      return (
        normalized === "1" || normalized === "true" || normalized === "correct"
      );
    }
    return false;
  };

  // Group data by subject & section
  const groupedData = useMemo(() => {
    if (!data.length) return [];
    const sections = [];
    let currentSection = null;
    data.forEach((q) => {
      const key = getExamSectionKey(q) || "Unknown";
      if (
        !currentSection ||
        currentSection.key !== key ||
        q.display_number === 1
      ) {
        currentSection = {
          key,
          heading: getExamSectionKey(q) || "Unknown",
          questions: [],
        };
        sections.push(currentSection);
      }
      currentSection.questions.push(q);
    });
    return sections;
  }, [data]);


  const statusCounts = useMemo(() => {
  const counts = {
    correct: 0,
    incorrect: 0,
    unattempted: 0,
    marked: 0,
  };

  data.forEach((q) => {
    const attempted = isQuestionAttempted(q);

    if (q.marked) {
      counts.marked++;
    } else if (!attempted) {
      counts.unattempted++;
    } else {
      isQuestionCorrect(q) ? counts.correct++ : counts.incorrect++;
    }
  });

  return counts;
}, [data]);

  if (!data.length)
    return <p style={{ textAlign: "center" }}>No questions found</p>;

  let globalIndex = 0;

//   const getClass = (q, globalI) => {
//   const isSelected = globalI === selectedIndex;

//   if (isSelected) return "selected";
//   if (q.marked) return "marked";

//   const attempted =
//     (q.selectedOption !== null && q.selectedOption !== undefined) ||
//     (q.integerAnswer && String(q.integerAnswer).trim() !== "");

//   if (!attempted) return "unattempted";

//   let isCorrect = false;

//   if (q.option && Array.isArray(q.option)) {
//     q.option.forEach((opt) => {
//       if (
//         String(opt.option_id) === String(q.selectedOption) &&
//         opt.is_correct === 1
//       ) {
//         isCorrect = true;
//       }
//     });
//   }

//   return isCorrect ? "correct" : "incorrect";
// };

  const getClass = (q) => {
  if (q.marked) return "marked";

  const attempted = isQuestionAttempted(q);

  if (!attempted) return "unattempted";

  return isQuestionCorrect(q) ? "correct" : "incorrect";
};

  return (
    <div className="questionGrid_wrapper">
      <h4 className="grid_heading">Your Response Grid</h4>
       <h4 className="grid_overview">overview</h4>

      {/* Status Legend with Counts Inside Circles */}
      <div className="overview">
        <div className="status_row">
          <span className="status_circle correct">{statusCounts.correct}</span>
          Correct
        </div>
        <div className="status_row">
          <span className="status_circle incorrect">{statusCounts.incorrect}</span>
          Incorrect
        </div>
        <div className="status_row">
          <span className="status_circle unattempted">{statusCounts.unattempted}</span>
          Unattempted
        </div>
        <div className="status_row">
          <span className="status_circle marked">{statusCounts.marked}</span>
          Marked for Review
        </div>
      </div>

      {/* Sections and Questions */}
      {groupedData.map((section, sectionIndex) => (
        <div key={sectionIndex} className="section_wrapper">
          <h5>{section.heading}</h5>
          <div className="grid">
            {section.questions.map((q) => {
              
              const index = globalIndex++;
              const className = getClass(q, index);

              const questionNumber =index+1; 
              return (
                <div
                  key={index}
                  className={`grid_item ${className}`}
                  onClick={() => onSelect(index)}
                  title={`Q${questionNumber}`}
                >
                  {questionNumber}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}